import React from "react";
import { Box, Paper, Typography, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";

const HeatmapContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(0.5),
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  border: "1px solid rgba(0,0,0,0.05)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

const HeatmapCell = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "intensity" &&
    prop !== "value" &&
    prop !== "cellWidth" &&
    prop !== "cellHeight",
})(({ theme, intensity, value, cellWidth = 32, cellHeight = 24 }) => ({
  width: `${cellWidth}px`,
  height: `${cellHeight}px`,
  minWidth: `${cellWidth}px`,
  minHeight: `${cellHeight}px`,
  margin: "1px",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "8px",
  fontWeight: "bold",
  color: intensity > 0.6 ? "white" : "black",
  backgroundColor: `rgba(25, 118, 210, ${intensity})`,
  cursor: "pointer",
  transition: "all 0.2s ease",
  border: "1px solid rgba(255,255,255,0.3)",
  "&:hover": {
    transform: "scale(1.1)",
    zIndex: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
    border: "2px solid #1976d2",
  },
}));

const LabelCell = styled(Box)(({ theme }) => ({
  width: "90px",
  minWidth: "90px",
  padding: "2px 6px",
  fontSize: "10px",
  fontWeight: 500,
  color: theme.palette.text.primary,
  textAlign: "right",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  height: "24px",
  backgroundColor: "rgba(0,0,0,0.02)",
  borderRadius: "4px",
  marginRight: "2px",
  border: "1px solid rgba(0,0,0,0.05)",
}));

const HeaderCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== "cellWidth",
})(({ theme, cellWidth = 32 }) => ({
  width: `${cellWidth}px`,
  minWidth: `${cellWidth}px`,
  height: "20px", // Reducir altura ya que no hay rotación
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "8px",
  fontWeight: 500,
  color: theme.palette.text.primary,
  margin: "1px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "center",
  backgroundColor: "rgba(0,0,0,0.02)",
  borderRadius: "0 0 4px 4px", // Cambiar border radius para parte inferior
  border: "1px solid rgba(0,0,0,0.05)",
}));

const MaterialHeatmapImproved = ({ data, title = "Matriz de Similitud" }) => {
  if (!data || !data.z || !data.x || !data.y) {
    return (
      <HeatmapContainer>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography>No hay datos disponibles</Typography>
      </HeatmapContainer>
    );
  }

  const { z: matrix, x: xLabels, y: yLabels } = data;

  // Encontrar min y max para normalizar
  const flatValues = matrix
    .flat()
    .filter((v) => v !== null && v !== undefined && !isNaN(v));
  const minValue = Math.min(...flatValues);
  const maxValue = Math.max(...flatValues);

  // Función para normalizar intensidad de color
  const getIntensity = (value) => {
    if (maxValue === minValue) return 0.5;
    return (value - minValue) / (maxValue - minValue);
  };

  // Función para truncar texto
  const truncateText = (text, maxLength = 12) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Ajustar automáticamente al tamaño de los datos
  // Mostrar todos los datos disponibles (sin limitación artificial)
  const limitedYLabels = yLabels; // Mostrar todas las filas
  const limitedXLabels = xLabels; // Mostrar todas las columnas
  const limitedMatrix = matrix; // Mostrar toda la matriz

  // Ajustar tamaño de celdas según cantidad de datos
  const cellSize = {
    width: Math.max(24, Math.min(32, 400 / xLabels.length)), // Entre 24px y 32px
    height: Math.max(20, Math.min(24, 300 / yLabels.length)), // Entre 20px y 24px
  };

  return (
    <HeatmapContainer>
      {title && (
        <Typography
          variant="h6"
          gutterBottom
          sx={{ mb: 1, fontWeight: "bold", fontSize: "1rem" }}
        >
          {title}
        </Typography>
      )}

      <Typography
        variant="caption"
        sx={{
          mb: 2,
          display: "block",
          color: "text.secondary",
          fontSize: "0.75rem",
        }}
      >
        Dimensiones: {yLabels.length} × {xLabels.length} | Rango:{" "}
        {minValue.toFixed(3)} - {maxValue.toFixed(3)}
      </Typography>

      <Box
        sx={{
          width: "100%",
          height: "auto", // Altura automática
          maxHeight: "450px", // Máximo para evitar que se haga demasiado alto
          overflowX: "auto",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            height: "6px",
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.05)",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "3px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "fit-content",
            gap: 0.25,
          }}
        >
          {/* Data rows */}
          {limitedMatrix.map((row, rowIndex) => (
            <Box key={rowIndex} sx={{ display: "flex", alignItems: "center" }}>
              {/* Row label */}
              <Tooltip title={limitedYLabels[rowIndex]} arrow>
                <LabelCell>
                  {truncateText(limitedYLabels[rowIndex], 12)}
                </LabelCell>
              </Tooltip>

              {/* Data cells */}
              {row.map((value, colIndex) => {
                const intensity = getIntensity(value);
                return (
                  <Tooltip
                    key={colIndex}
                    title={`${limitedYLabels[rowIndex]} → ${
                      limitedXLabels[colIndex]
                    }: ${value?.toFixed(3) || "N/A"}`}
                    arrow
                  >
                    <HeatmapCell
                      intensity={intensity}
                      value={value}
                      cellWidth={cellSize.width}
                      cellHeight={cellSize.height}
                    >
                      {value !== null && value !== undefined
                        ? value.toFixed(2)
                        : "—"}
                    </HeatmapCell>
                  </Tooltip>
                );
              })}
            </Box>
          ))}

          {/* Headers row - movido abajo */}
          <Box sx={{ display: "flex", alignItems: "flex-start", mt: 0.5 }}>
            {/* Empty corner cell */}
            <Box sx={{ width: "90px", height: "20px", mr: 1 }} />

            {/* Column headers */}
            {limitedXLabels.map((label, index) => (
              <Tooltip key={index} title={label} arrow>
                <HeaderCell cellWidth={cellSize.width}>
                  {truncateText(label, 6)}
                </HeaderCell>
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Leyenda de colores */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          pt: 1,
          borderTop: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 12,
              backgroundColor: "rgba(25, 118, 210, 0.1)",
              borderRadius: 1,
              border: "1px solid rgba(0,0,0,0.1)",
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontSize: "0.75rem", color: "text.secondary" }}
          >
            Baja similitud
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 12,
              backgroundColor: "rgba(25, 118, 210, 1)",
              borderRadius: 1,
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontSize: "0.75rem", color: "text.secondary" }}
          >
            Alta similitud
          </Typography>
        </Box>
      </Box>
    </HeatmapContainer>
  );
};

export default MaterialHeatmapImproved;
